import React, { Fragment, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { createReservation } from "../../api/reservation.api";
import { getVenues } from "../../api/venue.api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { generateTimeOptions, to12Hour } from "../../utils/datetime";

const ReservationCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState([]);

  const [form, setForm] = useState({
    name: "",
    phoneNo: "",
    email: "",
    venueId: "",
    date: "",
    startTime: "",
    endTime: "",
    status: "pending",
    paymentStatus: "not_applicable",

    type: "special_event",
    purpose: "",
    specialRequest: "",
  });

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues();
        setVenues(res.data || []);
      } catch (err) {
        toast.error("Failed to load venues");
      }
    };

    fetchVenues();
  }, []);

  const isNonBooking = form.type !== "booking";

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVenueChange = (venueId) => {
    const selected = venues.find((v) => v.id === parseInt(venueId));

    setForm((prev) => ({
      ...prev,
      venueId,
      paymentStatus: selected?.requiresPayment
        ? "pending"
        : "not_applicable",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.venueId) {
        return toast.error("Venue is required");
      }

      if (!form.date || !form.startTime || !form.endTime) {
        return toast.error("Date and time are required");
      }

      let payload = {
        ...form,
        venueId: Number(form.venueId),
      };

      if (isNonBooking) {
        payload = {
          ...payload,
          name: "-",
          phoneNo: "-",
          email: "-",
          status: "confirmed",
          paymentStatus: "not_applicable",
          specialRequest: "",
        };
      }

      setLoading(true);

      await createReservation(payload);

      toast.success("Reservation created successfully");
      navigate("/reservations");
    } catch (err) {
      toast.error(err.message || "Failed to create reservation");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reservation"))}
        title={titleCase(t("new reservation"))}
        mainTitle={titleCase(t("new reservation"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card className="mb-4">
              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>

                  {/* TYPE (BUTTON SELECTION) */}
                  <FormGroup>
                    <Label>{titleCase(t("reservation type"))}</Label>
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        color={form.type === "special_event" ? "primary" : "light"}
                        onClick={() =>
                          handleChange("type", "special_event")
                        }
                      >
                        {titleCase(t("special event"))}
                      </Button>

                      <Button
                        type="button"
                        color={form.type === "maintenance" ? "primary" : "light"}
                        onClick={() => handleChange("type", "maintenance")}
                      >
                        {titleCase(t("maintenance"))}
                      </Button>

                      <Button
                        type="button"
                        color={form.type === "booking" ? "primary" : "light"}
                        onClick={() => handleChange("type", "booking")}
                      >
                        {titleCase(t("booking"))}
                      </Button>
                    </div>
                  </FormGroup>

                  {/* COMMON FIELDS */}
                  <FormGroup>
                    <Label>{titleCase(t("venue"))}</Label>
                    <Input
                      type="select"
                      value={form.venueId}
                      onChange={(e) =>
                        handleVenueChange(e.target.value)
                      }
                      required
                    >
                      <option value="">
                        {titleCase(t("select venue"))}
                      </option>
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("purpose"))}</Label>
                    <Input
                      type="text"
                      value={form.purpose}
                      onChange={(e) =>
                        handleChange("purpose", e.target.value)
                      }
                    />
                  </FormGroup>

                  {/* BOOKING ONLY FIELDS */}
                  {!isNonBooking && (
                    <>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("name"))}</Label>
                            <Input
                              type="text"
                              value={form.name}
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              required
                            />
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("phone number"))}</Label>
                            <Input
                              type="text"
                              value={form.phoneNo}
                              onChange={(e) =>
                                handleChange("phoneNo", e.target.value)
                              }
                              required
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup>
                        <Label>{titleCase(t("email"))}</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          required
                        />
                      </FormGroup>

                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("status"))}</Label>
                            <Input
                              type="select"
                              value={form.status}
                              onChange={(e) =>
                                handleChange("status", e.target.value)
                              }
                            >
                              <option value="pending">
                                {titleCase(t("pending"))}
                              </option>
                              {/* <option value="approved">
                                {titleCase(t("approved"))}
                              </option> */}
                              <option value="confirmed">
                                {titleCase(t("confirmed"))}
                              </option>
                              <option value="cancelled">
                                {titleCase(t("cancelled"))}
                              </option>
                            </Input>
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("payment status"))}</Label>
                            <Input
                              type="select"
                              value={form.paymentStatus}
                              onChange={(e) =>
                                handleChange(
                                  "paymentStatus",
                                  e.target.value
                                )
                              }
                            >
                              <option value="pending">
                                {titleCase(t("pending"))}
                              </option>
                              <option value="paid">
                                {titleCase(t("paid"))}
                              </option>
                              <option value="not_applicable">
                                {titleCase(t("not applicable"))}
                              </option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}

                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("date"))}</Label>
                        <Input
                          type="date"
                          value={form.date}
                          onChange={(e) =>
                            handleChange("date", e.target.value)
                          }
                          required
                        />
                      </FormGroup>
                    </Col>

                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("start time"))}</Label>
                        <Input
                          type="select"
                          value={form.startTime}
                          onChange={(e) =>
                            handleChange("startTime", e.target.value)
                          }
                          required
                        >
                          <option value="">{titleCase(t("start time"))}</option>
                          {generateTimeOptions().map((time) => (
                            <option key={time} value={time}>
                              {to12Hour(time)}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("end time"))}</Label>
                        <Input
                          type="select"
                          value={form.endTime}
                          onChange={(e) =>
                            handleChange("endTime", e.target.value)
                          }
                          required
                        >
                          <option value="">{titleCase(t("end time"))}</option>
                          {generateTimeOptions().map((time) => (
                            <option key={time} value={time}>
                              {to12Hour(time)}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* SPECIAL REQUEST */}
                  {!isNonBooking && (
                    <FormGroup>
                      <Label>{titleCase(t("special request"))}</Label>
                      <Input
                        type="textarea"
                        value={form.specialRequest}
                        onChange={(e) =>
                          handleChange("specialRequest", e.target.value)
                        }
                      />
                    </FormGroup>
                  )}
                </Form>
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReservationCreate;